// src/components/InvoiceSection.js (FINAL - With Monthly Totals & Pagination)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppContext } from '../../App';
import { api } from '../../apiService';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';
import * as Sentry from "@sentry/react";

// --- START: InvoiceView Component (CORRECTED WITH ICONS) ---
function InvoiceView({ invoice, onBack }) {
    const { gstEnabled, gstRate, invoiceTerms, bankDetails } = useAppContext();
    const invoiceRef = useRef(null);
    const qrCodeRef = useRef(null);
    const [qrCodeDataURL, setQrCodeDataURL] = useState('');
    const [fullInvoiceData, setFullInvoiceData] = useState(invoice);

    useEffect(() => {
        if (!invoice.startDate && invoice.campaignId) {
            const fetchCampaignData = async () => {
                try {
                    const campaignData = await api.getById('ad_campaigns', invoice.campaignId);
                    if (campaignData) {
                        setFullInvoiceData(prevData => ({
                            ...prevData,
                            startDate: campaignData.startDate,
                            endDate: campaignData.endDate,
                        }));
                    }
                } catch (error) { console.error("Error fetching linked campaign data:", error); }
            };
            fetchCampaignData();
        }
    }, [invoice]);

    const subtotal = parseFloat(fullInvoiceData.totalAmount) || 0;
    const gstAmount = gstEnabled ? subtotal * gstRate : 0;
    const totalAmountDue = subtotal + gstAmount;
    const qrCodeValue = `https://billing.mixfm.pk/verify?invoiceNo=${fullInvoiceData.id}`;

    useEffect(() => {
        const timer = setTimeout(() => {
            if (window.QRious && qrCodeRef.current) {
                try {
                    new window.QRious({ element: qrCodeRef.current, value: qrCodeValue, size: 144, level: 'H', backgroundAlpha: 0, foreground: 'white' });
                    setQrCodeDataURL(qrCodeRef.current.toDataURL("image/png"));
                } catch(e) { console.error("QRious error:", e); }
            }
        }, 50);
        return () => clearTimeout(timer);
    }, [qrCodeValue, qrCodeRef]);

    const handlePrintAndDownload = (action) => {
        const input = invoiceRef.current;
        if (!input || !qrCodeDataURL) { toast.error("Component is not ready for export."); return; }
        toast.info(`Generating ${action}...`);
        setTimeout(() => {
            window.html2canvas(input, { scale: 3, useCORS: true, backgroundColor: '#ffffff' })
            .then((canvas) => {
                const imgData = canvas.toDataURL('image/png', 1.0);
                if (action === 'download') {
                    const { jsPDF } = window.jspdf;
                    const pdf = new jsPDF('p', 'mm', 'a4');
                    const pdfWidth = pdf.internal.pageSize.getWidth();
                    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
                    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
                    pdf.save(`invoice-${fullInvoiceData.id?.toString().substring(0,8)}.pdf`);
                } else if (action === 'print') {
                    const printWindow = window.open('', '', 'height=842,width=595');
                    printWindow.document.write('<html><head><title>Print Invoice</title>');
                    printWindow.document.write('<style>@page { size: A4; margin: 0; } body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }</style>');
                    printWindow.document.write('</head><body>');
                    printWindow.document.write(`<img src="${imgData}" style="width: 100%;">`);
                    printWindow.document.close();
                    setTimeout(() => { printWindow.focus(); printWindow.print(); printWindow.close(); }, 250);
                }
            }).catch(err => {
                toast.error("An error occurred while creating the file.");
                if (window.Sentry) Sentry.captureException(err);
            });
        }, 300);
    };

    const status = fullInvoiceData.status;
    let stampElement = null;
    if (status === 'Paid' || status === 'Unpaid' || status === 'Partially Paid') {
        let stampClasses = "text-5xl font-black uppercase border-4 rounded-md p-2 transform -rotate-6 opacity-20 select-none";
        let stampText = status;

        if (status === 'Paid') stampClasses += " text-green-600 border-green-600";
        else if (status === 'Unpaid') stampClasses += " text-red-600 border-red-600";
        else if (status === 'Partially Paid') { stampClasses += " text-yellow-500 border-yellow-500"; stampText = "Partial"; }
        
        stampElement = <div className={stampClasses}>{stampText}</div>;
    }
    
    return (
        <div>
             <div className="flex justify-between items-center mb-6">
                <button onClick={onBack} className="flex items-center bg-rose-600 text-white py-2 px-4 rounded-md hover:bg-rose-700 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Invoices
                </button>
                <div className="flex gap-2">
                    <button onClick={() => handlePrintAndDownload('print')} className="flex items-center bg-blue-500 text-white py-2 px-4 rounded-md disabled:bg-gray-400 hover:bg-blue-600 transition-colors" disabled={!qrCodeDataURL}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        {qrCodeDataURL ? 'Print' : 'Loading...'}
                    </button>
                    <button onClick={() => handlePrintAndDownload('download')} className="flex items-center bg-green-500 text-white py-2 px-4 rounded-md disabled:bg-gray-400 hover:bg-green-600 transition-colors" disabled={!qrCodeDataURL}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                           <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {qrCodeDataURL ? 'Download PDF' : 'Loading...'}
                    </button>
                </div>
            </div>

            <div className="max-w-4xl mx-auto">
                <div ref={invoiceRef} className="bg-white rounded-lg shadow-2xl p-8 font-sans border-t-8 border-red-600">
                    <header className="w-full mb-12 pb-6 border-b border-slate-200"><img src="/Header.png" alt="Flare Media Group Header" className="w-full h-auto" /></header>
                    <section className="grid grid-cols-2 gap-12 mb-8">
                        <div><h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Billed To</h3><p className="text-xl font-bold text-slate-900">{fullInvoiceData.clientName}</p><p className="text-slate-600">{fullInvoiceData.clientCompany}</p><p className="text-slate-600">{fullInvoiceData.clientAddress}</p></div>
                        <div className="text-right"><h1 className="text-4xl font-extrabold text-red-600 tracking-tight">INVOICE</h1><div className="text-sm text-slate-600 mt-2"><p><span className="font-semibold">Invoice No:</span> {fullInvoiceData.id?.toString().substring(0, 8)}</p><p><span className="font-semibold">Date:</span> {new Date(fullInvoiceData.invoiceDate).toLocaleDateString()}</p></div></div>
                    </section>
                    <div className="border rounded-lg overflow-hidden">
                        <table className="min-w-full text-sm">
                            <thead className="bg-red-600 text-white uppercase text-xs"><tr><th className="py-2 px-4 text-left font-semibold">Item & Description</th><th className="py-2 px-4 text-left font-semibold">Details</th><th className="py-2 px-4 text-right font-semibold">Amount</th></tr></thead>
                            <tbody className="divide-y divide-slate-200">
                                <tr className="text-slate-800"><td className="py-3 px-4 align-top"><p className="font-semibold">{fullInvoiceData.campaignName}</p><p className="text-xs text-slate-500">Main Advertising Package</p></td><td className="py-3 px-4 text-slate-600 align-top">{fullInvoiceData.startDate && fullInvoiceData.endDate ? `${new Date(fullInvoiceData.startDate).toLocaleDateString()} - ${new Date(fullInvoiceData.endDate).toLocaleDateString()}`: <span className="italic">N/A</span>}</td><td className="py-3 px-4 font-semibold text-right align-top">PKR {subtotal.toLocaleString()}</td></tr>
                                {(typeof fullInvoiceData.subCampaigns === 'string' ? JSON.parse(fullInvoiceData.subCampaigns) : fullInvoiceData.subCampaigns || []).map((part) => (<tr key={part.name} className="text-slate-800"><td className="py-1.5 px-4 pl-8 text-slate-600 align-top">{part.name}</td><td className="py-1.5 px-4 text-slate-600 align-top" colSpan="2">{part.timings?.join(', ')}</td></tr>))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-between items-center mt-6"><div className="flex-1 flex justify-center items-center">{stampElement}</div><div className="w-full max-w-sm text-sm"><div className="flex justify-between py-1.5"><span className="text-slate-600">Subtotal:</span><span className="font-medium text-slate-800">PKR {subtotal.toLocaleString('en-PK')}</span></div>{gstEnabled && (<div className="flex justify-between py-1.5 border-t border-slate-200"><span className="text-slate-600">GST ({gstRate * 100}%):</span><span className="font-medium text-slate-800">PKR {gstAmount.toLocaleString('en-PK')}</span></div>)}<div className="flex justify-between items-center bg-red-600 text-white font-bold p-3 rounded-lg mt-2"><span className="text-base">Total Amount Due:</span><span className="text-2xl">PKR {totalAmountDue.toLocaleString('en-PK')}</span></div></div></div>
                    <section className="mt-8 pt-6 border-t border-slate-200 text-sm text-slate-600"><div className="grid grid-cols-5 gap-8"><div className="col-span-2 text-center flex flex-col items-center bg-red-600 rounded-xl shadow-lg p-4"><h3 className="font-semibold text-white uppercase tracking-wider mb-3">Scan to Verify</h3><canvas ref={qrCodeRef} className="rounded-md" /><p className="text-xs text-red-200 mt-4">Invoice Tracking No.</p><code className="text-white text-sm font-bold tracking-wider break-all text-center">{fullInvoiceData.id}</code></div><div className="col-span-3 bg-slate-50 rounded-xl shadow-lg p-6 flex flex-col border border-slate-200"><div><h3 className="font-semibold text-slate-800 mb-2">Terms & Conditions</h3><p className="text-xs mb-4">{invoiceTerms}</p></div><div className="mt-auto"><h3 className="font-semibold text-slate-800 mb-2">Payment Details</h3><div className="text-xs bg-white p-3 rounded-lg border border-slate-200 whitespace-pre-wrap font-mono">{bankDetails}</div></div></div></div></section>
                    <footer className="mt-8 pt-6 border-t-2 border-slate-300"><p className="text-center text-xs text-slate-500 mb-4 italic">This is a computer-generated invoice and does not require a signature.</p><div className="relative bg-gray-900 h-16 flex items-center justify-center px-4 sm:px-8 overflow-hidden rounded-lg z-0"><div className="absolute inset-y-0 left-0 w-1/3 bg-red-600 transform -skew-x-12 origin-bottom-left -ml-16 z-10"></div><div className="absolute inset-y-0 right-0 w-1/3 bg-red-600 transform skew-x-12 origin-bottom-right -mr-16 z-10"></div><div className="relative z-20 flex w-full justify-between items-center text-white text-sm font-semibold"><p className="text-left flex-1">WWW.FLARE.PK</p><p className="text-center flex-1">0323-9999999</p><p className="text-right flex-1">Thank you for your business!</p></div></div></footer>
                </div>
            </div>
        </div>
    );
}
// --- END: InvoiceView Component ---


// --- START: Main InvoiceSection Component (With Pagination & Monthly Totals) ---
function InvoiceSection() {
    const { user, currentStationId } = useAppContext();
    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [deletingInvoice, setDeletingInvoice] = useState(null);
    const [filter, setFilter] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());

    // --- NEW STATE FOR MONTHLY TOTALS ---
    const [monthlyTotals, setMonthlyTotals] = useState({ amount: 0, paid: 0, pending: 0 });

    const [pageSize] = useState(15);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalInvoices, setTotalInvoices] = useState(0);

    const fetchInvoices = useCallback(async () => {
        if (!currentStationId || currentStationId === 'all') {
            setInvoices([]); setIsLoading(false); return;
        }
        setIsLoading(true);

        const startOfMonth = new Date(filterYear, filterMonth - 1, 1).toISOString().split('T')[0];
        const endOfMonth = new Date(filterYear, filterMonth, 0).toISOString().split('T')[0];
        
        try {
            const results = await api.get('invoices', { 
                where: { stationId: currentStationId }
            });

            const filtered = results.filter(inv => {
                const d = new Date(inv.invoiceDate).toISOString().split('T')[0];
                const matchesDate = d >= startOfMonth && d <= endOfMonth;
                const matchesStatus = filterStatus === 'all' || inv.status === filterStatus;
                const matchesSearch = !filter || inv.clientName.toLowerCase().includes(filter.toLowerCase()) || inv.id?.toString().includes(filter);
                return matchesDate && matchesStatus && matchesSearch;
            });

            setTotalInvoices(filtered.length);
            
            // Manual pagination
            const start = (currentPage - 1) * pageSize;
            const end = start + pageSize;
            setInvoices(filtered.slice(start, end));

            // Calculate totals
            const totals = filtered.reduce((acc, inv) => {
                acc.amount += (parseFloat(inv.totalAmount) || 0);
                acc.paid += (parseFloat(inv.totalPaid) || 0);
                acc.pending += (parseFloat(inv.pendingAmount) || 0);
                return acc;
            }, { amount: 0, paid: 0, pending: 0 });
            setMonthlyTotals(totals);

        } catch (err) {
            toast.error("Error fetching invoices.");
            console.error("API Error:", err);
        } finally {
            setIsLoading(false);
        }
    }, [currentStationId, filterYear, filterMonth, filterStatus, filter, currentPage, pageSize]);
    
    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    const handleDeleteInvoice = async () => {
        if (!deletingInvoice) return;
        try {
            await api.delete('invoices', deletingInvoice.id);
            toast.success("Invoice deleted successfully.");
            fetchInvoices();
        } catch (err) {
            toast.error("Failed to delete invoice.");
            console.error("Delete Error:", err);
        } finally {
            setDeletingInvoice(null);
        }
    };

    if (selectedInvoice) return <InvoiceView invoice={selectedInvoice} onBack={() => setSelectedInvoice(null)} />;

    return (
        <div className="container mx-auto">
            <ConfirmationModal isOpen={!!deletingInvoice} onClose={() => setDeletingInvoice(null)} onConfirm={handleDeleteInvoice} title="Delete Invoice">
                Are you sure you want to delete the invoice for <strong>{deletingInvoice?.clientName}</strong>? This cannot be undone.
            </ConfirmationModal>

            <div className="mb-8">
                <h1 className="text-4xl font-bold text-gray-800">Invoices</h1>
                <p className="text-gray-500 mt-1">Manage and view advertising invoices</p>
            </div>

            {/* --- MONTHLY TOTALS DASHBOARD --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-white p-6 rounded-2xl shadow-md border-l-4 border-blue-500">
                    <p className="text-sm font-semibold text-gray-500 uppercase">Total Invoiced</p>
                    <p className="text-2xl font-bold text-blue-600">PKR {monthlyTotals.amount.toLocaleString()}</p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-md border-l-4 border-green-500">
                    <p className="text-sm font-semibold text-gray-500 uppercase">Total Received</p>
                    <p className="text-2xl font-bold text-green-600">PKR {monthlyTotals.paid.toLocaleString()}</p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-md border-l-4 border-red-500">
                    <p className="text-sm font-semibold text-gray-500 uppercase">Pending Amount</p>
                    <p className="text-2xl font-bold text-red-600">PKR {monthlyTotals.pending.toLocaleString()}</p>
                </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-lg mb-8">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Search Client / Invoice No</label>
                        <input type="text" value={filter} onChange={e => { setFilter(e.target.value); setCurrentPage(1); }} placeholder="Search by name or ID..." className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-rose-500" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Status</label>
                        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }} className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-rose-500">
                            <option value="all">All Status</option>
                            <option value="Paid">Paid</option>
                            <option value="Unpaid">Unpaid</option>
                            <option value="Partially Paid">Partially Paid</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Month</label>
                        <select value={filterMonth} onChange={e => { setFilterMonth(parseInt(e.target.value)); setCurrentPage(1); }} className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-rose-500">
                            {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('default', {month: 'long'})}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Year</label>
                        <select value={filterYear} onChange={e => { setFilterYear(parseInt(e.target.value)); setCurrentPage(1); }} className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-rose-500">
                            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                            <tr>
                                <th className="px-6 py-3">Invoice No</th>
                                <th className="px-6 py-3">Client / Company</th>
                                <th className="px-6 py-3">Campaign</th>
                                <th className="px-6 py-3 text-right">Amount</th>
                                <th className="px-6 py-3 text-center">Status</th>
                                <th className="px-6 py-3 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {isLoading ? (
                                <tr><td colSpan="6" className="px-6 py-10 text-center">Loading invoices...</td></tr>
                            ) : invoices.length > 0 ? invoices.map(inv => (
                                <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 font-mono text-xs text-gray-400">#{inv.id?.toString().substring(0, 8)}</td>
                                    <td className="px-6 py-4">
                                        <div className="font-bold text-gray-800">{inv.clientName}</div>
                                        <div className="text-xs text-gray-500">{inv.clientCompany}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-gray-700">{inv.campaignName}</div>
                                        <div className="text-xs text-gray-400">{new Date(inv.invoiceDate).toLocaleDateString()}</div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="font-bold text-gray-900">PKR {(parseFloat(inv.totalAmount) || 0).toLocaleString()}</div>
                                        {parseFloat(inv.totalPaid) > 0 && <div className="text-xs text-green-600">Paid: PKR {parseFloat(inv.totalPaid).toLocaleString()}</div>}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                            inv.status === 'Paid' ? 'bg-green-100 text-green-700' :
                                            inv.status === 'Partially Paid' ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-red-100 text-red-700'
                                        }`}>{inv.status}</span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex justify-center space-x-2">
                                            <button onClick={() => setSelectedInvoice(inv)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="View/Print">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                            </button>
                                            {user?.role === 'admin' && (
                                                <button onClick={() => setDeletingInvoice(inv)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-400 italic">No invoices found for the selected criteria.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* --- PAGINATION CONTROLS --- */}
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-100">
                    <p className="text-xs text-gray-500">Showing <strong>{invoices.length}</strong> of <strong>{totalInvoices}</strong> invoices</p>
                    <div className="flex space-x-2">
                        <button 
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                            disabled={currentPage === 1}
                            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >Previous</button>
                        <button 
                            onClick={() => setCurrentPage(prev => prev + 1)} 
                            disabled={currentPage * pageSize >= totalInvoices}
                            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InvoiceSection;
